import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Flame, Eye, EyeSlash } from "@phosphor-icons/react";

export default function Login() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [loginIdentifier, setLoginIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await login(loginIdentifier, password);
      navigate("/", { replace: true });
    } catch (e) {
      const d = e?.response?.data?.detail;
      console.log(e);
      setErr(typeof d === "string" ? d : "Error al iniciar sesión");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-2"
      data-testid="login-page"
    >
      <div
        className="relative hidden lg:block overflow-hidden"
        style={{
          backgroundImage: 'url("/login.png")',
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/10 to-med-primary/30" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-xs uppercase tracking-[0.3em] mb-3 opacity-80">
            Fiesta · Tradición · Comunidad
          </div>
          <h2 className="font-display text-4xl xl:text-5xl font-bold leading-tight max-w-md">
            Tu falla, organizada como nunca.
          </h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-med-bg">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-4 mb-10">
            <div className="w-10 h-10 rounded-xl overflow-hidden grid place-items-center">
              <img
                src="/favicon.png"
                alt="Trakka"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <div className="font-display text-2xl font-bold text-med-text">
                Trakka
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-med-muted">
                Panel de administración
              </div>
            </div>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold text-med-text mb-2">
            Hola
          </h1>
          <p className="text-med-muted mb-10">
            Inicia sesión para acceder a los servicios.
          </p>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-med-text mb-2">
                Email
              </label>
              <input
                type="text"
                required
                value={loginIdentifier}
                onChange={(e) => setLoginIdentifier(e.target.value)}
                data-testid="login-identifier-input"
                className="w-full bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 rounded-xl px-4 py-3 outline-none transition-all"
                placeholder="nombre@correo.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-med-text mb-2">
                Contraseña
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="w-full bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 rounded-xl px-4 py-3 pr-12 outline-none transition-all"
                  placeholder="••••••••"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-med-muted hover:text-med-text transition-colors"
                >
                  {showPassword ? (
                    <EyeSlash size={20} weight="duotone" />
                  ) : (
                    <Eye size={20} weight="duotone" />
                  )}
                </button>
              </div>
              <div className="flex justify-end mt-2">
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="text-sm font-medium text-med-primary hover:underline"
                >
                  ¿Has olvidado tu contraseña?
                </button>
              </div>
            </div>

            {err && (
              <div
                data-testid="login-error"
                className="text-sm text-med-danger bg-med-danger/10 rounded-lg px-3 py-2"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-btn"
              className="w-full bg-med-primary hover:bg-med-primary-hover text-white rounded-xl py-3 font-semibold transition-colors active:scale-[0.98] disabled:opacity-60"
            >
              {submitting ? "Entrando…" : "Iniciar sesión"}
            </button>
          </form>

          <div className="mt-10 pt-6 border-t border-med-border text-center">
            <p className="text-xs text-med-muted mb-3">
              Sistema interno de gestión de la falla.
            </p>

            <Link
              to="/politica-privacidad"
              data-testid="privacy-policy-link"
              className="inline-flex items-center text-sm font-medium text-med-primary hover:underline"
            >
              Política de privacidad
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
